import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import SEO from '@/components/SEO';
import EstimateFormNative from '@/components/EstimateFormNative';
import TrashCanValetPage from '@/pages/TrashCanValetPage';
import TrashCanCleaningPage from '@/pages/TrashCanCleaningPage';
import { businessSchema, websiteSchema } from '@/lib/seo';
import pages from './pages.json';
import { Article, Blog, Gallery, Reviews, usePublishedContent } from './LiveContent';
import { bindDesignInteractions } from './interactions';
import './template.css';
import './integration.css';

const articleDesign = Object.values(pages).find(page => page.kind === 'article');

function ArticlePage({ page, pathname }) {
  const state = usePublishedContent(`blog/${encodeURIComponent(decodeURIComponent(pathname.slice(6)))}`);
  const post = state.data;
  const title = post?.seo_title || (post ? `${post.title} | Junk In Da Truck` : 'The Haul Journal | Junk In Da Truck');
  const description = post?.seo_description || post?.excerpt || page.description;
  const schema = post ? { '@context': 'https://schema.org', '@type': 'BlogPosting', headline: post.title, description, datePublished: post.published_at, author: { '@type': 'Organization', name: post.author || 'Junk In Da Truck' }, mainEntityOfPage: `https://junkindatruck.com${pathname}`, image: post.featured_image_url || page.image } : undefined;
  return <TemplatePage page={page} pathname={pathname} title={title} description={description} robots={state.notFound ? 'noindex, follow' : undefined} schema={schema} article={state} heroDescription={post?.excerpt} heading={post?.title || (state.notFound ? 'Article not found' : state.error ? 'Article unavailable' : 'The Haul Journal')} />;
}

function TemplatePage({ page, pathname, title, description, robots, schema, article, heading, heroDescription }) {
  const { search, hash } = useLocation();
  const laborRequest = new URLSearchParams(search).get('service') === 'manual-labor';
  const root = useRef(null);
  const [slots, setSlots] = useState([]);
  const markup = useMemo(() => ({ __html: page.html }), [page.html]);
  useLayoutEffect(() => {
    const node = root.current;
    const hosts = Array.from(node.querySelectorAll('[data-live-slot]'));
    const unbindInteractions = bindDesignInteractions(node);
    setSlots(hosts);
    const menu = node.querySelector('.menu-toggle');
    const nav = node.querySelector('#mobile-nav');
    const close = () => { if (!nav || !menu) return; nav.hidden = true; menu.setAttribute('aria-expanded', 'false'); menu.setAttribute('aria-label', 'Open navigation'); };
    const toggle = () => { const open = menu.getAttribute('aria-expanded') === 'true'; nav.hidden = open; menu.setAttribute('aria-expanded', String(!open)); menu.setAttribute('aria-label', open ? 'Open navigation' : 'Close navigation'); };
    const escape = event => {
      if (event.key !== 'Escape') return;
      const active = document.activeElement;
      const focusedDropdown = active?.closest('.nav-drop[open]');
      const inMobileNav = nav?.contains(active);
      close();
      node.querySelectorAll('.nav-drop[open]').forEach(el => { el.open = false; });
      if (inMobileNav) menu?.focus();
      else focusedDropdown?.querySelector('summary')?.focus();
    };
    const outside = event => node.querySelectorAll('.nav-drop[open]').forEach(el => { if (!el.contains(event.target)) el.open = false; });
    const dropdowns = Array.from(node.querySelectorAll('.desktop-nav>.nav-drop'));
    const exclusive = event => {
      if (event.target.open) dropdowns.forEach(el => { if (el !== event.target) el.open = false; });
    };
    dropdowns.forEach(el => el.addEventListener('toggle', exclusive));
    menu?.addEventListener('click', toggle);
    document.addEventListener('keydown', escape);
    document.addEventListener('click', outside);
    return () => { unbindInteractions(); dropdowns.forEach(el => el.removeEventListener('toggle', exclusive)); menu?.removeEventListener('click', toggle); document.removeEventListener('keydown', escape); document.removeEventListener('click', outside); };
  }, [page.html]);
  useEffect(() => {
    if (!hash || !slots.length) return;
    // The live form mounts through a portal after the static page is inserted.
    // Restore deep links once that content has finished mounting.
    const frame = requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView());
    return () => cancelAnimationFrame(frame);
  }, [hash, slots]);
  useEffect(() => {
    if (heading) {
      root.current.querySelector('h1').textContent = heading;
      const breadcrumb = root.current.querySelector('.breadcrumb');
      if (breadcrumb?.lastChild) breadcrumb.lastChild.textContent = heading;
      const art = root.current.querySelector('.hero-art');
      if (art) art.alt = `${heading} — Junk In Da Truck journal`;
    }
    if (heroDescription) root.current.querySelector('.hero-description').textContent = heroDescription;
  }, [heading, heroDescription]);
  return <>
    <SEO title={title || page.title} description={description || page.description} path={pathname} image={page.image} robots={robots} schema={schema || [businessSchema, websiteSchema()]} />
    <div ref={root} className="redesign-template" dangerouslySetInnerHTML={markup} />
    {slots.map((host, index) => {
      let content;
      switch (host.dataset.liveSlot) {
        case 'gallery': content = <Gallery />; break;
        case 'gallery-preview': content = <Gallery preview />; break;
        case 'reviews': content = <Reviews />; break;
        case 'reviews-preview': content = <Reviews preview />; break;
        case 'blog': content = <Blog />; break;
        case 'article': content = <Article state={article} />; break;
        case 'estimate': content = <EstimateFormNative preferredCategory={laborRequest ? 'Manual Labor' : undefined} />; break;
        case 'valet': content = <div className="recurring-form"><TrashCanValetPage embedded /></div>; break;
        case 'cleaning': content = <div className="recurring-form"><TrashCanCleaningPage embedded /></div>; break;
        default: content = null;
      }
      return createPortal(content, host, `${pathname}-${index}`);
    })}
  </>;
}

function MissingPage({ pathname }) {
  return <><SEO title="Page not found | Junk In Da Truck" path={pathname} robots="noindex, follow" /><div className="redesign-template"><main className="section"><p className="eyebrow">404</p><h1>That page has moved.</h1><p>Let’s help you find what you need.</p><a className="button" href="/">Back to home ↗</a> <a className="text-link" href="/estimate">Get an estimate ↗</a></main></div></>;
}

export default function RedesignPage() {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/index\.html$/, '').replace(/\/+$/, '') || '/';
  const page = pages[pathname];
  if (pathname.startsWith('/blog/')) return <ArticlePage key={pathname} pathname={pathname} page={articleDesign} />;
  if (!page) return <MissingPage pathname={pathname} />;
  return <TemplatePage key={pathname} page={page} pathname={pathname} />;
}
